import { Link, useParams } from 'react-router-dom'
import { usePublicCaptain } from '@/api/hooks'
import { useAuth } from '@/auth/useAuth'
import { ReputationIcon, RANK_NAMES } from '@/components/game/ReputationIcon'
import { CaptainPortrait } from '@/components/game/cosmetics/CaptainPortrait'
import { CaptainBlason } from '@/components/game/cosmetics/CaptainBlason'
import { ReputationCompass } from '@/components/ReputationCard'
import { StatTile } from '@/components/game/ui'
import {
  GoldIcon, PurseIcon, CombatIcon, CompassIcon, AnchorIcon, PrestigeIcon, TrophyIcon,
  StatIcon, ChevronRight, PennantIcon,
} from '@/components/game/GameIcons'
import type { ReputationDto } from '@/api/types'

/**
 * Warm accent per faction — tints the hero medallion frame. Mirrors the private
 * livret de bord (Captain.tsx) so the public vitrine reads as its twin.
 */
const FACTION_RING: Record<string, string> = {
  marine: 'border-ocean-300/60',
  guilde: 'border-sand-300/60',
  corsaire: 'border-sand-500/60',
  pirate: 'border-rum-500/60',
  neutral: 'border-ocean-500/50',
}

/**
 * Faction-tinted pill for the hero `faction · rang` badge (proto §8.1 — "fond/
 * bordure teintés faction"). Real palette shades only (ocean-{100,300,500,700,900},
 * sand-{100,300,500}, rum-{500,700}); the neutral tone matches a ghost badge.
 */
const FACTION_BADGE: Record<string, string> = {
  marine: 'bg-ocean-700/30 border-ocean-300/50 text-ocean-100',
  guilde: 'bg-sand-300/15 border-sand-300/45 text-sand-100',
  corsaire: 'bg-sand-500/20 border-sand-500/50 text-sand-100',
  pirate: 'bg-rum-700/30 border-rum-500/50 text-sand-100',
  neutral: 'bg-ocean-900/40 border-sand-500/15 text-ocean-300',
}

/**
 * /capitaine/:pseudo — a captain's PUBLIC livret de bord: the read-only, expurgated
 * mirror of the private /capitaine page. Hero (giant ReputationIcon + flavored rank
 * title + prestige honorific + emergent-reputation compass, all in ONE banner like
 * the private page), faits d'armes, the
 * season "registre" (FLUX-only — never stock/fleet/defenses, anti-grief) and the
 * captain's leaderboard placements. Fed by usePublicCaptain(pseudo); the backend
 * already sanitizes — the page only renders what it gets.
 */
export function PublicCaptain() {
  const { pseudo } = useParams<{ pseudo: string }>()
  const { user } = useAuth()
  const { data, isLoading, isError } = usePublicCaptain(pseudo)

  if (isLoading) {
    return (
      <section className="max-w-4xl mx-auto p-4 sm:p-6">
        <p className="text-ocean-100">Chargement du livret de bord…</p>
      </section>
    )
  }

  if (isError || !data) {
    return (
      <section className="max-w-4xl mx-auto p-4 sm:p-6 space-y-3">
        <p className="text-rum-500">Ce capitaine est introuvable.</p>
        <Link to="/classements" className="inline-flex items-center gap-1 text-sm text-sand-300 hover:text-sand-100 transition-colors">
          <span aria-hidden="true">‹</span> Classements
        </Link>
      </section>
    )
  }

  const rep: ReputationDto = data.reputation
  const isNeutral = rep.faction === 'neutral'
  const ring = FACTION_RING[rep.faction] ?? FACTION_RING.neutral
  const since = new Date(data.createdAt).toLocaleDateString('fr-FR', { year: 'numeric', month: 'long' })
  // Backend rank tier is 0..5 (0 = neutral) → the RANK_NAMES art index is rank-1.
  const rankName = RANK_NAMES[Math.max(0, Math.min(4, rep.rank - 1))]

  // The 6 SEASON FLUX stats (never stock/fleet/defenses). Icons mirror the proto's
  // glyphs: gold · purse · crossed-swords · shield · compass · anchor.
  const stats = [
    { key: 'plunder', icon: <GoldIcon className="w-6 h-6 text-sand-300" />, value: data.seasonStats.plunder, label: 'Butin PvP' },
    { key: 'commerceGold', icon: <PurseIcon className="w-6 h-6 text-sand-300" />, value: data.seasonStats.commerceGold, label: 'Gains du commerce' },
    { key: 'raidsWon', icon: <CombatIcon className="w-6 h-6 text-sand-300" />, value: data.seasonStats.raidsWon, label: 'Raids gagnés' },
    { key: 'raidsRepelled', icon: <StatIcon name="armure" className="w-6 h-6 text-sand-300" />, value: data.seasonStats.raidsRepelled, label: 'Raids repoussés' },
    { key: 'islandsDiscovered', icon: <CompassIcon className="w-6 h-6 text-sand-300" />, value: data.seasonStats.islandsDiscovered, label: 'Îles découvertes' },
    { key: 'shipsBuilt', icon: <AnchorIcon className="w-6 h-6 text-sand-300" />, value: data.seasonStats.shipsBuilt, label: 'Navires construits' },
  ]

  const pavillonLabel = isNeutral ? 'Sans pavillon' : `${rep.factionLabel} · ${rankName}`

  // Le viewer regarde SON propre livret → mêmes affordances que la page privée
  // (médaillon cliquable vers la cabine + bouton « Cabine du capitaine »).
  const isSelf = !!user && data.pseudo === user.pseudo

  // médaillon partagé self/other — 68px en mobile (carte d'identité), 128px dès sm.
  // Portrait peint configuré : PAS de cadre carré autour (le médaillon circulaire
  // porte déjà son anneau + cadre de cabine — retour utilisateur T18) ; le conteneur
  // passe en rounded-full pour que la lueur Légende épouse le cercle.
  const medallionClass = data.appearance?.avatar
    ? 'relative grid place-items-center w-[68px] h-[68px] sm:w-32 sm:h-32 shrink-0 rounded-full'
    : `relative grid place-items-center w-[68px] h-[68px] sm:w-32 sm:h-32 shrink-0 rounded-2xl bg-ocean-900/60 border-2 ${ring}`
  const medallionGlow = !isNeutral && rep.rank === 5
    ? { boxShadow: '0 0 0 1px rgba(229,204,143,.33), 0 0 40px -10px rgba(229,204,143,.55)' }
    : undefined
  // Les ReputationIcon gardent leurs 104px exacts dès sm (desktop inchangé) et
  // remplissent le cadre 68px en mobile.
  const medallionContent = data.appearance?.avatar
    ? <CaptainPortrait config={data.appearance.avatar} size={104} className="w-full h-full" />
    : isNeutral
      ? <ReputationIcon faction="neutral" size={104} className="w-full h-full sm:w-[104px] sm:h-[104px]" title="Sans pavillon" />
      : <ReputationIcon faction={rep.faction} rank={rep.rank} size={104} className="w-full h-full sm:w-[104px] sm:h-[104px]" title={rep.title} />

  return (
    <section className="max-w-4xl mx-auto p-4 sm:p-6 pb-20 space-y-7">
      <Link to="/classements" className="inline-flex items-center gap-1.5 text-sm text-ocean-300 hover:text-sand-300 transition-colors">
        <span aria-hidden="true">‹</span> Classements
      </Link>

      {/* ——— Bannière héros (modelée sur le livret de bord privé) ——— */}
      <section className="relative overflow-hidden rounded-2xl border border-sand-500/20 bg-gradient-to-b from-ocean-700/30 to-[#06222b]/45 p-6 sm:p-8">
        {/* filigrane rose des vents */}
        <div className="pointer-events-none absolute -right-12 -top-14 w-60 h-60 opacity-[0.06]" aria-hidden="true">
          <svg viewBox="0 0 200 200" className="w-full h-full" fill="none">
            <g stroke="#E5CC8F"><circle cx="100" cy="100" r="92" strokeWidth="0.6" /><circle cx="100" cy="100" r="60" strokeWidth="0.5" /></g>
            <g transform="rotate(0 100 100)"><path d="M100,16 L92,90 L100,100 Z" fill="#F5EDD7" /><path d="M100,16 L108,90 L100,100 Z" fill="#C9A55C" /></g>
            <g transform="rotate(90 100 100)"><path d="M100,16 L92,90 L100,100 Z" fill="#F5EDD7" /><path d="M100,16 L108,90 L100,100 Z" fill="#C9A55C" /></g>
            <g transform="rotate(180 100 100)"><path d="M100,16 L92,90 L100,100 Z" fill="#F5EDD7" /><path d="M100,16 L108,90 L100,100 Z" fill="#C9A55C" /></g>
            <g transform="rotate(270 100 100)"><path d="M100,16 L92,90 L100,100 Z" fill="#F5EDD7" /><path d="M100,16 L108,90 L100,100 Z" fill="#C9A55C" /></g>
          </svg>
        </div>

        {/* Agencement mobile B « carte d'identité » : rangée compacte médaillon
            (68px) + colonne identité alignée en HAUT ; en dessous, blason et
            prestige en deux tuiles jumelles ; puis (self) bouton Cabine pleine
            largeur. Desktop (sm:+) : bannière flex historique inchangée. */}
        <div className="relative flex flex-col sm:flex-row sm:items-center gap-5">
          <div className="flex items-start sm:items-center gap-4 sm:gap-5 min-w-0 sm:flex-1">
            {/* médaillon EN GRAND dans un cadre bordé faction — lueur dorée au rang
                Légende. Sur SON propre profil : cliquable vers la cabine (plume au
                survol), comme sur la page privée. */}
            {isSelf ? (
              <Link
                to="/capitaine/cabine"
                aria-label="Modifier mon apparence"
                className={`group ${medallionClass} hover:border-sand-300 transition-colors`}
                style={medallionGlow}
              >
                {medallionContent}
                {/* plume d'édition — n'apparaît qu'au survol du médaillon */}
                <span
                  aria-hidden="true"
                  className="absolute -bottom-1.5 -right-1.5 grid place-items-center w-6 h-6 rounded-full bg-ocean-900 border border-sand-300/60 opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 text-sand-300" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M19.5 4.5c-4.5.4-8.3 2.8-10.3 6.8L6.5 16l6.7-2.7c4-2 6-5.8 6.3-8.8Z" />
                    <path d="M6.5 16 4 20" />
                  </svg>
                </span>
              </Link>
            ) : (
              <div className={medallionClass} style={medallionGlow}>
                {medallionContent}
              </div>
            )}

            {/* blason équipé — desktop uniquement : en mobile il vit dans la
                tuile jumelle « Blason » plus bas (petit bloc dupliqué, assumé). */}
            {data.appearance?.blason && (
              <div className="hidden sm:block shrink-0 self-center" title="Blason du capitaine">
                <CaptainBlason config={data.appearance.blason} size={56} />
              </div>
            )}

            <div className="min-w-0">
              <p className="text-[11px] uppercase tracking-[0.32em] text-ocean-300 mb-1 sm:mb-1.5">Livret de bord</p>
              <h1 className="font-display text-2xl sm:text-5xl text-sand-300 leading-none break-words">{data.pseudo}</h1>
              {!isNeutral && rep.flavorTitle && (
                <p className="font-display text-base sm:text-lg text-sand-100 mt-1.5 sm:mt-2">{rep.flavorTitle}</p>
              )}
              <div className="mt-2.5 sm:mt-3 flex items-center gap-2 flex-wrap">
                {isNeutral ? (
                  <span className="text-xs px-2.5 py-1 rounded-full border bg-ocean-900/40 border-sand-500/20 text-ocean-300">Sans pavillon</span>
                ) : (
                  <span className={`text-xs px-2.5 py-1 rounded-full border ${FACTION_BADGE[rep.faction] ?? FACTION_BADGE.neutral}`}>{rep.factionLabel} · {rankName}</span>
                )}
                {/* Titre ÉQUIPÉ à la cabine — remplace les titres auto (streak). */}
                {data.appearance?.titleLabel && (
                  <span className="text-xs px-2.5 py-1 rounded-full border bg-sand-300/15 border-sand-300/40 text-sand-200">{data.appearance.titleLabel}</span>
                )}
                <span className="text-xs px-2.5 py-1 rounded-full border bg-ocean-900/40 border-sand-500/15 text-ocean-300">Membre depuis {since}</span>
              </div>
              {/* bouton Cabine (self) — version desktop ; en mobile c'est la
                  rangée pleine largeur en bas de la carte. */}
              {isSelf && (
                <Link
                  to="/capitaine/cabine"
                  className="hidden sm:inline-flex items-center gap-2 mt-3 px-4 py-2 rounded-xl border border-sand-300/40 bg-sand-300/10 text-sand-200 hover:bg-sand-300/20 transition-colors text-sm"
                >
                  Cabine du capitaine
                  <span aria-hidden="true">→</span>
                </Link>
              )}
            </div>
          </div>

          {/* boîte prestige — desktop ; en mobile : tuile jumelle plus bas. */}
          <div className="hidden sm:block self-auto sm:ml-auto shrink-0 text-center px-5 py-3.5 rounded-xl bg-ocean-900/50 border border-sand-500/20">
            <PrestigeIcon className="w-6 h-6 mx-auto text-sand-300" />
            <p className="font-display text-3xl text-sand-100 tabular-nums mt-1">{data.prestigePoints.toLocaleString('fr-FR')}</p>
            <p className="text-[10px] uppercase tracking-wider text-ocean-300">Prestige</p>
            <p className="text-[11px] text-sand-300 mt-1.5 font-medium">{data.prestigeTitle}</p>
          </div>
        </div>

        {/* Rangée 2 (mobile) — tuiles jumelles blason + prestige, moitié-moitié. */}
        <div className="relative mt-4 grid grid-cols-2 gap-3 sm:hidden">
          <div className="flex flex-col items-center justify-center gap-1.5 rounded-xl bg-ocean-900/50 border border-sand-500/20 px-3 py-3">
            {data.appearance?.blason ? (
              <CaptainBlason config={data.appearance.blason} size={52} />
            ) : (
              <span className="grid place-items-center w-[52px] h-[52px]" aria-hidden="true">
                <PennantIcon className="w-7 h-7 text-ocean-300/50" />
              </span>
            )}
            <p className="text-[10px] uppercase tracking-wider text-ocean-300">Blason</p>
          </div>
          <div className="flex flex-col items-center justify-center gap-1 rounded-xl bg-ocean-900/50 border border-sand-500/20 px-3 py-3 text-center">
            <PrestigeIcon className="w-6 h-6 text-sand-300" />
            <p className="font-display text-2xl text-sand-100 tabular-nums leading-none">{data.prestigePoints.toLocaleString('fr-FR')}</p>
            <p className="text-[10px] uppercase tracking-wider text-ocean-300">Prestige</p>
            <p className="text-[11px] text-sand-300 font-medium">{data.prestigeTitle}</p>
          </div>
        </div>

        {/* Rangée 3 (mobile, self) — entrée Cabine pleine largeur. */}
        {isSelf && (
          <Link
            to="/capitaine/cabine"
            className="relative sm:hidden mt-3 flex w-full items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-sand-300/40 bg-sand-300/10 text-sand-200 hover:bg-sand-300/20 transition-colors text-sm"
          >
            Cabine du capitaine
            <span aria-hidden="true">→</span>
          </Link>
        )}

        {/* réputation émergente — repliée dans la même bannière (plus de bloc séparé, cf. Captain.tsx) */}
        <div className="relative mt-7 pt-6 border-t border-sand-500/15">
          <div className="flex flex-col sm:flex-row sm:items-center gap-5">
            <div className="min-w-0 sm:flex-1">
              <p className="text-[11px] uppercase tracking-[0.25em] text-ocean-300">Réputation émergente</p>
              <h2 className="font-display text-2xl text-sand-100 mt-0.5">{pavillonLabel}</h2>
              {rep.description && <p className="text-sm text-ocean-100 mt-1 max-w-md">{rep.description}</p>}
            </div>
            <div className="self-center sm:self-auto shrink-0">
              <ReputationCompass reputation={rep} />
            </div>
          </div>
        </div>
      </section>

      {/* ——— Registre de la saison (FLUX uniquement) ——— */}
      <div>
        <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
          <h2 className="font-display text-lg text-sand-100">Registre de la saison</h2>
          <span className="text-[11px] uppercase tracking-[0.2em] text-ocean-300">
            {data.season?.name ?? 'Saison'} · cette saison
          </span>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
          {stats.map((s) => (
            <StatTile key={s.key} icon={s.icon} value={s.value.toLocaleString('fr-FR')} label={s.label} />
          ))}
        </div>
        <p className="text-xs text-ocean-300/80 mt-2.5">
          Seuls les <span className="text-ocean-100">exploits de la saison</span> sont publics.
          Les réserves, la flotte et les défenses de l'île restent secrètes.
        </p>
      </div>

      {/* ——— Au tableau d'honneur ——— */}
      <div>
        <h2 className="font-display text-lg text-sand-100 mb-3">Au tableau d'honneur</h2>
        {data.boards.length > 0 ? (
          <div className="grid sm:grid-cols-2 gap-3">
            {data.boards.map((b) => (
              <Link
                key={b.key}
                to="/classements"
                className="flex items-center gap-3 px-4 py-3 rounded-xl border border-sand-500/15 bg-ocean-900/30 hover:border-sand-300/45 hover:bg-ocean-700/20 transition-colors"
              >
                <TrophyIcon className="w-5 h-5 text-sand-300 shrink-0" />
                <span className="font-display text-sand-100 flex-1">{b.label}</span>
                <span className="font-display tabular-nums text-sand-300">{b.place === 1 ? '1ʳᵉ' : `${b.place}ᵉ`}</span>
                <ChevronRight className="w-4 h-4 text-ocean-300" />
              </Link>
            ))}
          </div>
        ) : (
          <div className="rounded-2xl border border-sand-500/20 bg-gradient-to-b from-ocean-700/30 to-[#06222b]/40 p-6 text-center">
            <PrestigeIcon className="w-7 h-7 mx-auto text-ocean-300/70" />
            <p className="text-sm text-ocean-100 mt-3">Pas encore classé. Ses premiers exploits le porteront bientôt sur les tableaux.</p>
            <Link to="/classements" className="inline-block mt-3 text-sm text-sand-300 hover:text-sand-100 transition-colors">
              Voir les classements ›
            </Link>
          </div>
        )}
      </div>

      {/* ——— Faits d'armes (en dernier) ——— */}
      {/* Palmarès de SAISON, et libellé comme tel : ce bloc dérive de
          completed_objectives, que le reset décoche (fiche 31). Sans le dire, un
          vétéran verrait ses distinctions fondre d'un coup sur la seule page que
          les autres capitaines consultent. Le palmarès de carrière, ce sont les
          titres et le prestige, déjà rendus plus haut. */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <h2 className="font-display text-lg text-sand-100">Faits d'armes de la saison</h2>
          {data.faitsDArmes.length > 0 && (
            <span className="text-xs text-ocean-300">
              {data.faitsDArmes.length} distinction{data.faitsDArmes.length > 1 ? 's' : ''}
            </span>
          )}
        </div>
        <p className="text-[13px] text-ocean-300 mb-3 leading-relaxed">
          Un palmarès de saison : ces distinctions se rejouent au prochain reset. Ce qui reste gravé, ce sont les{' '}
          <span className="text-ocean-100">titres et le prestige</span>.
        </p>
        {data.faitsDArmes.length > 0 ? (
          <div className="rounded-2xl border border-sand-500/20 bg-gradient-to-b from-ocean-700/30 to-[#06222b]/40 divide-y divide-sand-500/10">
            {data.faitsDArmes.map((f) => (
              <div key={f.key} className="flex items-start gap-3.5 px-4 sm:px-5 py-3.5">
                <span className="grid place-items-center w-9 h-9 shrink-0 rounded-lg bg-ocean-900/50 border border-sand-500/25 mt-0.5">
                  <TrophyIcon className="w-5 h-5 text-sand-300" />
                </span>
                <div className="min-w-0">
                  <p className="font-display text-[15px] leading-snug text-sand-100">{f.title}</p>
                  <p className="text-[13px] text-ocean-100 mt-0.5 leading-relaxed">{f.description}</p>
                </div>
              </div>
            ))}
            {data.faitsDArmes.length < 3 && (
              <div className="px-4 sm:px-5 py-4">
                <p className="text-sm text-ocean-300 italic">
                  Le reste de sa légende reste à écrire. Chaque traversée, chaque abordage forgera de nouveaux faits d'armes.
                </p>
              </div>
            )}
          </div>
        ) : (
          <div className="rounded-2xl border border-sand-500/20 bg-gradient-to-b from-ocean-700/30 to-[#06222b]/40 px-5 py-6">
            <p className="text-sm text-ocean-300 italic">Le reste de sa légende reste à écrire…</p>
          </div>
        )}
      </div>
    </section>
  )
}

export default PublicCaptain
